import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const TOKEN_KEY = "st_admin_token";
const USER_KEY = "st_admin_user";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(USER_KEY);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [verifying, setVerifying] = useState(Boolean(token));

  // Verify token on mount; clear if invalid
  useEffect(() => {
    let alive = true;
    if (!token) { setVerifying(false); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!alive) return;
        setUser(r.data);
        localStorage.setItem(USER_KEY, JSON.stringify(r.data));
      } catch (err) {
        if (!alive) return;
        // Only clear session on auth failures; transient network errors should not log the user out.
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          setToken(null);
          setUser(null);
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      } finally {
        if (alive) setVerifying(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: u } = r.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setToken(access_token);
    setUser(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  };

  const value = useMemo(() => ({ token, user, verifying, login, logout, isAdmin: user?.role === "admin" }),
    [token, user, verifying]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
